import {
  Directive,
  HostListener,
  Renderer2,
  AfterContentChecked,
  Output,
  EventEmitter,
  Input,
} from '@angular/core';

interface ColumnProps {
  width: number;
  class: string;
}

@Directive({
  selector: '[acaResizeColumns]',
  standalone: false,
})
export class ResizeColumnsDirective implements AfterContentChecked {
  @Input() public columnNames: string[];
  @Output() public changeSortableFlag = new EventEmitter<boolean>();

  private subscriptions: Array<() => void> = [];
  private readonly resizedColumnsProperties: ColumnProps[] = [];
  private parentClassName: string;
  private pressed: boolean;
  private startX: number;
  private startWidth: number;
  private headerElementForResizing: HTMLElement;

  constructor(public readonly renderer: Renderer2) {}

  public ngAfterContentChecked(): void {
    if (this.resizedColumnsProperties) {
      this.resizedColumnsProperties.forEach((item) => {
        this.updateWidth(item.width, item.class);
      });
    }
  }

  @HostListener('click', ['$event'])
  public onClickMethod(): void {
    this.changeSortableFlag.emit(true);
  }

  @HostListener('mousedown', ['$event'])
  public onMouseDown(event: MouseEvent): void {
    // onMouseDown should work only when we click on pseudo-element. Pseudo-element has width=11px.
    const target = event.target as HTMLElement;
    if (
      target.className.includes('adf-datatable-table-cell-header') &&
      target.offsetWidth - event.offsetX < 12
    ) {
      this.pressed = true;
      this.startX = event.x;
      this.headerElementForResizing = target;
      this.startWidth = this.headerElementForResizing.offsetWidth;

      this.columnNames.forEach((className: string) => {
        if (this.headerElementForResizing.className.includes(className)) {
          this.parentClassName = className;
        }
      });

      this.defineLastColumnWidth();

      const mousemoveListener = this.renderer.listen(
        document,
        'mousemove',
        this.mouseMoveMethod.bind(this)
      );
      const mouseUpListener = this.renderer.listen(
        document,
        'mouseup',
        this.mouseUpMethod.bind(this)
      );
      this.subscriptions.push(mousemoveListener, mouseUpListener);
    }
  }

  private defineLastColumnWidth(): void {
    let lastColumnClassName;
    const lastColumnChildren = document
      .getElementsByClassName(this.parentClassName)
      .item(0).parentElement.children;
    const lastColumnElement = lastColumnChildren[lastColumnChildren.length - 1];

    this.columnNames.forEach((className: string) => {
      if (lastColumnElement.className.includes(className)) {
        lastColumnClassName = className;
      }
    });

    if (
      !this.resizedColumnsProperties.find(
        (item) => item.class === lastColumnClassName
      )
    ) {
      this.resizedColumnsProperties.push({
        width: lastColumnElement.clientWidth,
        class: lastColumnClassName,
      });
    }

    this.updateWidth(lastColumnElement.clientWidth, lastColumnClassName);
  }

  private updateResizedColumnsProperties(width: number): void {
    const element = this.resizedColumnsProperties.find(
      (item) => item.class === this.parentClassName
    );

    if (this.resizedColumnsProperties.length && element) {
      element.width = width < 100 ? 100 : width;
    } else {
      this.resizedColumnsProperties.push({
        width,
        class: this.parentClassName,
      });
    }
  }

  private updateWidth(width: number, className: string): void {
    Array.from(document.getElementsByClassName(className)).forEach(
      (element: HTMLDivElement) => {
        element.style.width = `${width}px`;
        if (
          element.parentNode.children.item(
            element.parentNode.children.length - 1
          ) === element
        ) {
          element.parentElement.style.width = 'fit-content';
          element.style.flexGrow = '0';
        }
      }
    );
  }

  private mouseMoveMethod(event: MouseEvent): void {
    this.changeSortableFlag.emit(false);
    const width = this.startWidth + (event.x - this.startX);
    this.updateWidth(width, this.parentClassName);
    this.updateResizedColumnsProperties(width);
  }

  private mouseUpMethod(): void {
    this.pressed = this.pressed && !this.pressed;
    this.subscriptions.forEach((listener) => listener());
    this.subscriptions = [];
  }
}
